import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { api, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { WorkerCard } from "@/components/WorkerCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function WorkerDirectory() {
  const { t, lang, tenant } = useApp();
  usePageTitle(t("directory.workersTitle")); // RF-21: per-page tab title
  const [params, setParams] = useSearchParams();
  const [workers, setWorkers] = useState([]);
  const [query, setQuery] = useState(params.get("q") || "");
  // Without these, a failed request left `workers` at [] and rendered "0 workers" plus the
  // no-results box — a network error wearing an empty state's clothes, which tells a citizen
  // their city has no plumbers when in fact nobody asked the server successfully.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    if (!tenant) return;
    setLoading(true);
    setError(false);
    api
      .get("/directory/workers", { params: { tenant_id: tenant.id, q: params.get("q") || undefined } })
      .then((r) => setWorkers(rows(r.data)))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tenant, params]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    const next = new URLSearchParams(params);
    if (query) next.set("q", query);
    else next.delete("q");
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
      <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">{t("directory.workersTitle")}</h1>
      <p className="mt-2 max-w-2xl text-base text-muted-foreground">
        {t("directory.workersSubtitle", { city: localized(lang, tenant, "name") })}
      </p>

      <div className="mt-8 flex max-w-xl items-center gap-2 rounded-full border border-border bg-card p-2">
        <Search size={18} className="ml-3 text-muted-foreground" />
        <Input
          data-testid="worker-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applySearch()}
          placeholder={t("directory.workersSearchPlaceholder")}
          className="border-0 bg-transparent shadow-none focus-visible:ring-0"
        />
        <Button className="rounded-full px-5" data-testid="worker-search-btn" onClick={applySearch}>
          {t("common.search")}
        </Button>
      </div>

      {!loading && !error && (
        <p className="mt-8 text-sm font-medium text-muted-foreground" data-testid="worker-result-count">
          {t("directory.workerResultCount", { count: workers.length })}
        </p>
      )}

      {loading ? (
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="worker-loading">
          <Skeleton className="h-44 w-full rounded-2xl" />
          <Skeleton className="h-44 w-full rounded-2xl" />
          <Skeleton className="h-44 w-full rounded-2xl" />
        </div>
      ) : error ? (
        <div
          className="mt-8 flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-10 text-center text-sm text-destructive"
          data-testid="worker-error"
        >
          {t("directory.workersFailed")}
          <Button size="sm" variant="outline" className="rounded-full" data-testid="worker-retry" onClick={load}>
            {t("common.retry")}
          </Button>
        </div>
      ) : workers.length === 0 ? (
        <p className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="worker-empty">
          {t("common.noResults")}
        </p>
      ) : (
        <div className="mt-4 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {workers.map((w) => (
            <WorkerCard key={w.id} worker={w} />
          ))}
        </div>
      )}
    </div>
  );
}
